import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { RoundingHelper, Kpi, BulletChartOptions } from '../../wtFramework';

import {
    KpiConfiguration,
    BusinessKpi,
    IBindable,
    NameBuilderConfiguration,
    IKpiViewModel,
    SelectorType,
    IdentifierType,
    IValueViewModel,
    GetAggregatedValuesDto,
    Range,
    WidgetValues,
    ActualDataType,
    TagDefinition,
    Attributes
} from '../../models';
import {
    ITagDefinitionService,
    IGroupService,
    ISeriesSelectorService,
    IIdentifierVmService,
    ISeriesService,
    IRequestService,
    INameBuilderService,
} from '../../services';
import { BusinessKpiConfiguration } from '../../widgets/businessKpi/businessKpi.configuration';

import { errorAxisColor, errorBackgroundColor, errorTextColor, infoAxisColor, noBackgroundColor, infoTextColor, reconciledValues, validatedValues, warningAxisColor, warningBackgroundColor } from '../../common/constants';
import { ISeriesBuilder, ISeriesBuilderArguments } from './ISeriesBuilder';
import { SeriesMappingResult } from './';
import { mapConfiguration } from '../../common/helpers/valueViewModelHelper';
import { getPopoverCallback } from '../../widgets/common/callback';
import { popoverItem, getUnitName } from '../../common';

declare type roundingHelper = RoundingHelper;
declare type kpi = Kpi;
declare type bulletChartOptions = BulletChartOptions;

export interface KpiWithOption extends kpi {
    options: bulletChartOptions;
    attributes: Attributes;
}

export interface IBusinessKpiService extends ISeriesBuilder<IKpiViewModel> {
    getSeries(guid: string, isBindable?: boolean): angular.IPromise<KpiConfiguration>;
    getViewModels(kpis: KpiConfiguration[], nameConfig: NameBuilderConfiguration): angular.IPromise<IKpiViewModel[]>;
    readData(
        configuration: BusinessKpiConfiguration,
        interval: any,
        seriesConfiguration: IBindable[]
    ): angular.IPromise<BusinessKpi[]>;
    bindData(boundData: WidgetValues, kpiList: IKpiViewModel[]): KpiWithOption[];
}

export class BusinessKpiService implements IBusinessKpiService {

    static serviceId = 'businessKpiService';
    private _roundingHelper: roundingHelper;
    private _statuses = ['Ok', 'Warning', 'Error'];

    static $inject = [
        '$q',
        '$translate',
        '$state',
        'tagDefinitionService',
        'groupService',
        'seriesSelectorService',
        'identifierVmService',
        'seriesService',
        'requestService',
        'nameBuilderService'
    ];

    constructor(
        private _$q: angular.IQService,
        private $translate: angular.translate.ITranslateService,
        private _$state: angular.ui.IStateService,
        private _tagDefinitionService: ITagDefinitionService,
        private _groupService: IGroupService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _identifierVmService: IIdentifierVmService,
        private _seriesService: ISeriesService,
        private _requestService: IRequestService,
        private _nameBuilderService: INameBuilderService
    ) {
        this._roundingHelper = new RoundingHelper();
    }

    getSeries(guid: string, isBindable?: boolean): angular.IPromise<KpiConfiguration> {
        const defered = this._$q.defer<KpiConfiguration>();
        this._seriesSelectorService.getViewModel(guid)
            .then(vm => {
                const values = this._seriesSelectorService.getValues(vm as any);

                const kpi = {
                    'guid': guid,
                    kpiType: vm.type,
                    'color': infoTextColor,
                    value: values && values.length > 0 ? values[0].key : 'average',
                    isTargetVisible: true,
                    isRangeVisible: true,
                    isStateVisible: true,
                    isQualityVisible: true,
                    isTendencyVisible: true,
                    decimals: 2,
                    isAutoRange: true,
                    isBoundToGroup: vm.type === SelectorType.identifier && !!isBindable,
                    originalGuid: guid,
                    identifierType: vm.identifierType,
                    hasReconciledValue: vm.hasReconciledValue,
                    isConstraint: vm.isConstraint
                };
                if (kpi.hasReconciledValue) {
                    kpi.value = reconciledValues[0].key;
                } else if (vm.identifierType === IdentifierType.Validated) {
                    kpi.value = validatedValues[0].key;
                }
                this._seriesService.bind(kpi, vm.groupAbsolutePath);
                defered.resolve(kpi);
            });
        return defered.promise;
    }

    getViewModels(kpis: KpiConfiguration[], nameConfig: NameBuilderConfiguration): angular.IPromise<IKpiViewModel[]> {
        const defered = this._$q.defer<IKpiViewModel[]>();
        let converted: IKpiViewModel[] = [];
        if (kpis && kpis.length > 0) {
            this._identifierVmService.getViewModelsFromApiFromGuidList(kpis.map(d => d.guid))
                .then(responses => {
                    converted = kpis.map(k => {
                        const vm = _.find(responses, r => r.guid === k.guid);
                        if (vm) {
                            return this.mapViewModel(vm, k, nameConfig);
                        } else {
                            return {
                                options: {
                                    isContextVisible: false,
                                    isTendencyVisible: false,
                                    isQualityVisible: false,
                                    isTargetVisible: false,
                                    isRangeVisible: false,
                                    decimals: 0,
                                    valueFormat: () => ''
                                },
                                kpi: {
                                    color: errorTextColor,
                                    name: ''
                                } as any,
                                dataType: null,
                                viewModel: null
                            };
                        }
                    });
                    defered.resolve(converted);
                })
                .catch(error => {
                    defered.reject(error);
                });
        } else {
            defered.resolve([]);
        }

        return defered.promise;
    }

    private mapViewModel(vm: IValueViewModel, k: KpiConfiguration, nameConfig: NameBuilderConfiguration) {
        const kpi = this.createKpi(vm, k, nameConfig);
        const format = d3.format(`2.${k.decimals}f`);
        const valueFormat = (vm.actualDataType === ActualDataType.Digital || vm.actualDataType === ActualDataType.String) && k.value === 'interpolated'
            ? d => d.comment ? d.comment : format(d.value)
            : d => format(d.value);
        const options = {
            isContextVisible: k.isStateVisible,
            isTendencyVisible: k.isTendencyVisible,
            isQualityVisible: k.isQualityVisible,
            isTargetVisible: k.isTargetVisible && !!vm.target,
            isRangeVisible: k.isRangeVisible,
            decimals: k.decimals,
            valueFormat: valueFormat
        };
        if (k.isAutoRange || (!k.isAutoRange && !k.min && !k.max)) {
            this.setMinMax(k, kpi, vm);
        }
        return {
            options: options,
            kpi: kpi,
            dataType: vm.actualDataType,
            viewModel: vm
        };
    }

    private createKpi(vm: IValueViewModel,
        k: KpiConfiguration, config: NameBuilderConfiguration): kpi {
        const name = this._nameBuilderService.getName(vm, config, k as any);
        const serieName = this._nameBuilderService.getName(vm, { hasGroupName: true, hasName: true } as any, k as any);
        const unitName = getUnitName(vm.unitName, k.value);
        const kpi: kpi = {
            value: 0,
            target: vm.target,
            min: vm.min,
            max: vm.max,
            name: name,
            globalMin: k.min,
            globalMax: k.max,
            unit: unitName,
            quality: '',
            variation: 0,
            context: '',
            color: k.color,
            coloringConditions: [
                {
                    colorBack: errorBackgroundColor,
                    colorAxis: errorAxisColor,
                    condition: (d: kpi) => d.quality === this.$translate
                        .instant(this._statuses[2])
                },
                {
                    colorBack: warningBackgroundColor,
                    colorAxis: warningAxisColor,
                    condition: (d: kpi) => d.quality === this.$translate
                        .instant(this._statuses[1])
                },
                {
                    colorBack: noBackgroundColor,
                    colorAxis: infoAxisColor,
                    condition: (d: kpi) => !d.globalMin && !d.globalMax
                }
            ],
            identifier: k.value + vm.guid
        };
        (kpi as any).serieName = serieName;
        return kpi;
    }

    readData(
        configuration: BusinessKpiConfiguration,
        interval: any,
        seriesConfiguration: IBindable[]
    ): angular.IPromise<BusinessKpi[]> {
        const beginDate = interval.beginMoment.toISOString();
        const endDate = interval.endMoment.toISOString();
        const config: GetAggregatedValuesDto = {
            beginDateTime: beginDate,
            endDateTime: endDate,
            aggregationInterval: null,
            aggregationMode: configuration.aggregationMode,
            aggregationUnit: null,
            hasQuality: true,
            list: seriesConfiguration.map(r => {
                return {
                    guid: r.guid,
                    value: (r && r.value) ? r.value : 'average'
                };
            })
        };

        return this._requestService
            .cachedPostAtV1<BusinessKpi[]>('aggregatedValues/getBusinessKpis', config);
    }

    private setMinMax(
        k: KpiConfiguration,
        chartingKpi: kpi,
        vm: IValueViewModel
    ) {
        const defered = this._$q.defer<Range>();

        if (k.kpiType === SelectorType.tagDefinition) {
            this._tagDefinitionService.getByGuid(k.guid)
                .then(data => {
                    const tagDefinition: TagDefinition = data.tagDefinition;
                    let min = vm.min;
                    let max = vm.max;
                    if (min || min === 0) {
                        if (tagDefinition.min || tagDefinition.min === 0) {
                            min = Math.min(tagDefinition.min, vm.min);
                        }
                    } else {
                        min = tagDefinition.min;
                    }
                    if (max || max === 0) {
                        if (tagDefinition.max || tagDefinition.max === 0) {
                            max = Math.max(tagDefinition.max, vm.max);
                        }
                    } else {
                        max = tagDefinition.max;
                    }
                    defered.resolve({ min: min, max: max });
                });
        } else if (k.kpiType === SelectorType.identifier) {
            this._groupService.getIdentifierGlobalMinMax(k.guid).then(range => defered.resolve(range));
        }

        defered.promise.then((range) => {
            let min = range.min;
            let max = range.max;
            if (chartingKpi.target || chartingKpi.target === 0) {
                min = Math.min(min, chartingKpi.target);
                max = Math.max(max, chartingKpi.target);
            }
            const rounded = this._roundingHelper.round(min, max);
            chartingKpi.globalMin = rounded.min;
            chartingKpi.globalMax = rounded.max;
        });
    }

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult<IKpiViewModel>> {
        const { configuration, viewModels, map, errors } = args;
        const seriesConfig: KpiConfiguration[] = configuration.kpis;
        const mapped = mapConfiguration(seriesConfig as any[], viewModels, map, errors);
        const error = mapped.error;
        const series = mapped.series
            .filter(s => !s.missing)
            .map(s => this.mapViewModel(s.viewModel, s.configuration as any, configuration.nameConfiguration));
        return this._$q.when({
            error,
            series: series,
            map
        });
    }

    bindData(boundData: WidgetValues, kpiList: IKpiViewModel[]): KpiWithOption[] {
        kpiList.forEach(kpiVM => {
            const result: any = {};
            const kpi = kpiVM.kpi;
            for (const key of Object.keys(kpi)) {
                result[key] = kpi[key];
            }
            let identifier = kpi.identifier;
            const value = _.find(boundData.aggregatedValue, v => v.valueType + v.guid === identifier);
            let valueType = '';
            if (value) {
                valueType = value.valueType;
                result.value = (value.value as any) === 'NaN' ? null : value.value;
                result.comment = this._statuses[value.comment];
            } else {
                result.value = null;
            }
            identifier = identifier.replace(valueType, '');
            const quality = _.find(boundData.statuses, t => t.key === identifier);
            //If no quality, no error
            result.quality = quality ? this.$translate.instant(this._statuses[quality.value]) : 'OK';
            const variation = _.find(boundData.tendencies, t => t.key === identifier);
            result.variation = variation ? variation.value : 0;
            const context = _.find(boundData.contexts, t => t.key === identifier);
            result.context = context ? context.value : '';
            kpiVM.kpi = result;
        });
        const kpiVmList = kpiList
            .map(d => {
                const vm = d.viewModel;
                const callback = getPopoverCallback(vm, this._$state.go);
                const result: any = angular.copy(d.kpi);
                result.options = d.options;
                result.attributes = {
                    'wt-popover-svg': true,
                    template: vm.objectType === 0
                        ? 'tagPopover.html'
                        : 'identifierPopover.html',
                    item: popoverItem(vm),
                    'compile': 'wt-popover-svg'
                };
                result.onClick = callback;
                return result;
            });
        return kpiVmList;
    }
}